import type { FC, FormEvent, ReactNode } from 'react'
import type { ScoutApplication, ScoutField } from '@/lib/applications'
import type { FieldErrors } from '@/lib/validate'

import { useRef, useState } from 'react'
import { Button } from '@/components/generals/button-link'
import { FormSuccess, Honeypot } from '@/components/forms/application-shell'
import { ChoiceGroup, TextAreaField, TextField } from '@/components/forms/fields'
import { SCOUT_DISCIPLINES, VISIBILITY, validateScoutApplication } from '@/lib/applications'
import { routes } from '@/constants/site'

type Status = 'idle' | 'sending' | 'sent'

const EMPTY: ScoutApplication = {
    fullName: '',
    email: '',
    currentRole: '',
    portfolioUrl: '',
    disciplines: [],
    recommendation: '',
    visibility: ''
}

const FIELD_ORDER: ScoutField[] = ['fullName', 'email', 'currentRole', 'portfolioUrl', 'disciplines', 'recommendation', 'visibility']

export const ScoutForm: FC = (): ReactNode => {
    const [values, setValues] = useState<ScoutApplication>(EMPTY)
    const [errors, setErrors] = useState<FieldErrors<ScoutField>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formError, setFormError] = useState('')
    const [nickname, setNickname] = useState('')
    const formRef = useRef<HTMLFormElement>(null)

    const update = <K extends ScoutField>(field: K, value: ScoutApplication[K]) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const focusFirstError = (found: FieldErrors<ScoutField>) => {
        const first = FIELD_ORDER.find(field => found[field])

        if (!first) return

        const target = formRef.current?.querySelector<HTMLElement>(`#${first}`)
            ?? formRef.current?.querySelector<HTMLElement>(`input[name="${first}"]`)

        target?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const { errors: found } = validateScoutApplication({ ...values })

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setFormError('Please check the highlighted fields.')
            focusFirstError(found)

            return
        }

        setStatus('sending')
        setFormError('')

        try {
            const response = await fetch('/api/applications/scout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, nickname })
            })
            const result = await response.json().catch(() => null) as { message?: string, errors?: FieldErrors<ScoutField> } | null

            if (!response.ok) {
                if (result?.errors) {
                    setErrors(result.errors)
                    focusFirstError(result.errors)
                }

                setFormError(result?.message ?? 'Something went wrong. Please try again.')
                setStatus('idle')

                return
            }

            setStatus('sent')
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        catch {
            setFormError('We could not reach our server. Check your connection and try again.')
            setStatus('idle')
        }
    }

    if (status === 'sent') {
        const firstName = values.fullName.split(' ')[0]

        return (
            <FormSuccess
                title={`Thank you, ${firstName}. Your application is in.`}
                body={`Our team reads every application personally. We'll reply to ${values.email} within two weeks, and if it feels like a fit we'll suggest a short call.`}
                primary={{ label: 'Read the blog', href: routes.blog }}
                secondary={{ label: 'Back to Become a Scout', href: routes.becomeAScout }}
            />
        )
    }

    return (
        <form ref={formRef} noValidate onSubmit={handleSubmit} className='relative flex flex-col gap-7' aria-describedby={formError ? 'scout-form-error' : undefined}>
            <Honeypot value={nickname} onChange={setNickname} />

            <div className='grid gap-7 sm:grid-cols-2 sm:gap-5'>
                <TextField id='fullName' label='Your name' autoComplete='name' value={values.fullName} onChange={event => update('fullName', event.target.value)} error={errors.fullName} />
                <TextField id='email' label='Email' type='email' autoComplete='email' value={values.email} onChange={event => update('email', event.target.value)} error={errors.email} />
            </div>

            <TextField
                id='currentRole'
                label='What you do, and where'
                placeholder='Design Director at a product studio'
                value={values.currentRole}
                onChange={event => update('currentRole', event.target.value)}
                error={errors.currentRole}
            />

            <TextField
                id='portfolioUrl'
                label='Link to your work'
                inputMode='url'
                placeholder='yourportfolio.com'
                hint='A portfolio, a personal site or a profile where your work lives.'
                value={values.portfolioUrl}
                onChange={event => update('portfolioUrl', event.target.value)}
                error={errors.portfolioUrl}
            />

            <ChoiceGroup
                name='disciplines'
                legend='Which kinds of designers do you know best?'
                options={SCOUT_DISCIPLINES}
                multiple
                value={values.disciplines}
                onChange={value => update('disciplines', value)}
                hint='Choose as many as apply.'
                error={errors.disciplines}
            />

            <TextAreaField
                id='recommendation'
                label='Tell us about a designer you would recommend'
                rows={5}
                hint='Share their name only if you want to. We are most interested in why you rate their work.'
                value={values.recommendation}
                onChange={event => update('recommendation', event.target.value)}
                error={errors.recommendation}
            />

            <ChoiceGroup
                name='visibility'
                legend='Should your name travel with your introductions?'
                options={VISIBILITY}
                value={values.visibility ? [values.visibility] : []}
                onChange={value => update('visibility', value[0] ?? '')}
                error={errors.visibility}
            />

            <div className='flex flex-col gap-4 border-t border-ink/15 pt-7 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-[22rem] text-[12.5px] leading-snug text-ink/55'>
                    We use your details only to review your application and reply to you.
                </p>

                <Button type='submit' size='lg' arrow disabled={status === 'sending'} className='shrink-0'>
                    {status === 'sending' ? 'Sending' : 'Send application'}
                </Button>
            </div>

            <p id='scout-form-error' role='alert' className='-mt-3 min-h-[1.25rem] text-[13px] text-clay-deep'>
                {formError}
            </p>
        </form>
    )
}
